const vivaShadeFences = new WeakSet();
const decodeVivaShadeImage = async image => {
  image.loading = 'eager';
  if (typeof image.decode === 'function') {
    try { await image.decode(); return image.naturalWidth > 0; }
    catch (_) { return false; }
  }
  if (image.complete) return image.naturalWidth > 0;
  return new Promise(resolve => {
    const finish = () => {
      image.removeEventListener('load', finish);
      image.removeEventListener('error', finish);
      resolve(image.naturalWidth > 0);
    };
    image.addEventListener('load', finish);
    image.addEventListener('error', finish);
  });
};
const prepareVivaShadeFence = async (fence, loadImage = decodeVivaShadeImage) => {
  fence.classList.add('is-preparing');
  const images = Array.from(fence.querySelectorAll('.viva-shade-strip__image'));
  const groups = new Map();
  for (const image of images) {
    const source = [image.getAttribute('src'), image.getAttribute('srcset'), image.getAttribute('sizes')].join('|');
    if (!groups.has(source)) groups.set(source, []);
    groups.get(source).push(image);
  }
  await Promise.all(Array.from(groups.values(), async copies => {
    let loaded = false;
    try {
      const ready = await Promise.all(copies.map(image => loadImage(image)));
      loaded = ready.every(Boolean);
    } catch (_) {}
    for (const image of copies) {
      const strip = image.closest('.viva-shade-strip');
      strip?.classList.toggle('is-loaded', loaded);
      // Failed sources are removed from every repetition, keeping the loop equal.
      if (!loaded) strip?.remove();
    }
  }));
  fence.classList.add('is-ready');
};
const vivaHomeDemos = new WeakSet();
const initVivaHome = () => {
  document.querySelectorAll('.viva-shade-fence').forEach(fence => {
    if (vivaShadeFences.has(fence)) return;
    vivaShadeFences.add(fence);
    void prepareVivaShadeFence(fence);
  });
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tryOnDemos = document.querySelectorAll('[data-tryon-demo]');

  tryOnDemos.forEach((demo) => {
    if (vivaHomeDemos.has(demo)) return;
    vivaHomeDemos.add(demo);
    const stage = demo.querySelector('[data-tryon-stage]');
    if (!(stage instanceof HTMLElement)) return;

    const autoplay = demo.dataset.tryonAuto !== 'false';
    const slider = stage.querySelector('[data-tryon-slider]');
    const afterImage = demo.querySelector('[data-tryon-after-image]');
    const selectedCode = demo.querySelector('[data-tryon-selected-code]');
    const options = demo.querySelectorAll('[data-tryon-option]');
    const minPos = 14;
    const maxPos = 86;
    const period = 5200;
    const resumeDelay = 900;
    const tau = Math.PI * 2;

    let pos = 54;
    let manual = false;
    let rafId = null;
    let startTs = null;
    let resumeTimer = null;

    const clamp = (value, lo, hi) => Math.min(Math.max(value, lo), hi);
    const applyPos = (next) => {
      pos = clamp(next, 0, 100);
      stage.style.setProperty('--tryon-pos', `${pos.toFixed(2)}%`);
      if (slider instanceof HTMLInputElement && document.activeElement !== slider) slider.value = String(Math.round(pos));
    };

    const selectOption = (option) => {
      if (!(option instanceof HTMLButtonElement)) return;
      if (afterImage instanceof HTMLImageElement) {
        afterImage.src = option.dataset.tryonAfter || afterImage.src;
        afterImage.hidden = false;
      }
      if (selectedCode instanceof HTMLElement) selectedCode.textContent = option.dataset.tryonCode || '';
      options.forEach((item) => {
        if (!(item instanceof HTMLButtonElement)) return;
        const isActive = item === option;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    options.forEach((option) => {
      if (option instanceof HTMLButtonElement) option.addEventListener('click', () => selectOption(option));
    });

    const stopAuto = () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = null;
      startTs = null;
    };
    const startAuto = () => {
      if (!demo.isConnected || reduceMotion || !autoplay || manual || rafId !== null) return;
      const norm = clamp((pos - minPos) / (maxPos - minPos), 0, 1);
      const phaseSeed = Math.acos(1 - 2 * norm) / tau;
      const loop = (timestamp) => {
        if (!demo.isConnected) { stopAuto(); return; }
        if (startTs === null) startTs = timestamp - phaseSeed * period;
        const phase = ((timestamp - startTs) % period) / period;
        applyPos(minPos + (maxPos - minPos) * (0.5 - 0.5 * Math.cos(phase * tau)));
        rafId = window.requestAnimationFrame(loop);
      };
      rafId = window.requestAnimationFrame(loop);
    };
    const engageManual = () => {
      manual = true;
      stopAuto();
      if (resumeTimer !== null) window.clearTimeout(resumeTimer);
      resumeTimer = null;
    };
    const scheduleResume = () => {
      if (reduceMotion || !autoplay) return;
      if (resumeTimer !== null) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => { manual = false; resumeTimer = null; startAuto(); }, resumeDelay);
    };

    if (slider instanceof HTMLInputElement) {
      slider.addEventListener('input', () => { engageManual(); applyPos(parseFloat(slider.value)); });
      slider.addEventListener('focus', engageManual);
      slider.addEventListener('blur', scheduleResume);
      slider.addEventListener('pointerdown', engageManual);
    }
    stage.addEventListener('pointerenter', engageManual);
    stage.addEventListener('pointerleave', scheduleResume);
    window.addEventListener('pointerup', () => { if (manual) scheduleResume(); });

    if (reduceMotion || !autoplay) applyPos(50);
    else { applyPos(pos); startAuto(); }
  });
};
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', initVivaHome);
  document.addEventListener('shopify:section:load', initVivaHome);
}
if (typeof module !== 'undefined' && module.exports) module.exports = {prepareVivaShadeFence};
